import DownloadIcon from "@mui/icons-material/Download";
import { Typography, Button, CircularProgress, Alert } from "@mui/material";
import { GridToolbarContainer } from "@mui/x-data-grid";
import { useState, useEffect } from "react";

import { useExpandedEcrAssignmentQuery } from "api/memberPortal/reports/expandedEcrAssignment/expandedEcrAssignment.api";

import { useExpandedEcrExport } from "./hooks/useExpandedEcrExport";

import type { GridRowModel } from "@mui/x-data-grid";

interface ExpandedEcrToolbarProps {
  rowCount: number;
  rows: GridRowModel[];
  sidebarFilters: any;
  tableParams: any;
}

const ExpandedEcrToolbar = ({
  rowCount,
  rows,
  sidebarFilters,
  tableParams,
}: ExpandedEcrToolbarProps) => {
  const { exportToCsv, isExporting, alert, clearAlert } =
    useExpandedEcrExport();
  const [shouldFetchForExport, setShouldFetchForExport] = useState(false);

  // Auto-clear alert after 5 seconds
  useEffect(() => {
    if (alert?.message) {
      const timer = setTimeout(() => {
        clearAlert();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [alert, clearAlert]);

  // Use the query hook to fetch all data for export
  const { data, isLoading: isExportLoading } = useExpandedEcrAssignmentQuery(
    {
      ...sidebarFilters,
      ...tableParams,
      limit: rowCount,
      offset: 0,
    },
    {
      skip: !shouldFetchForExport, // Only run when we need to export
    },
  );

  // Watch for when the export query completes
  useEffect(() => {
    if (shouldFetchForExport && !isExportLoading && data?.results) {
      exportToCsv(data.results, "expanded-ecr-assignment-report").finally(
        () => {
          setShouldFetchForExport(false);
        },
      );
    }
  }, [shouldFetchForExport, isExportLoading, data]);

  const handleExport = () => {
    setShouldFetchForExport(true);
  };

  return (
    <GridToolbarContainer sx={{ p: 2 }}>
      {/* WARN: Custom styling from pre standardization! */}
      {/* TODO: Standardize when you come across this!  */}
      <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
        Expanded ECR Assignment Report: {rowCount}{" "}
        {rowCount === 1 ? "Match" : "Matches"}
      </Typography>
      <Button
        variant="outlined"
        startIcon={
          isExporting ? <CircularProgress size={16} /> : <DownloadIcon />
        }
        onClick={handleExport}
        disabled={isExporting || rows.length === 0}
        sx={{ ml: 2 }}
      >
        {isExporting ? "Exporting..." : "Export CSV"}
      </Button>
      {alert?.message && (
        <Alert
          severity={alert.type as "success" | "error"}
          variant="standard"
          sx={{ width: "100%", mt: 1 }}
        >
          {alert.message}
        </Alert>
      )}
    </GridToolbarContainer>
  );
};

export default ExpandedEcrToolbar;
